import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import styled from 'styled-components';
import { FiTrash2, FiEdit2, FiX, FiSave } from 'react-icons/fi';

const Container = styled.div`
  padding: 6rem 5% 4rem;
  min-height: 100vh;
  background: #f8f8f8;
`;

const AdminContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
  }
`;

const FormSection = styled(motion.div)`
  background: white;
  padding: 2rem;
  border-radius: 16px;
  box-shadow: var(--shadow-md);
`;

const ProductsSection = styled(motion.div)`
  background: white;
  padding: 2rem;
  border-radius: 16px;
  box-shadow: var(--shadow-md);
  max-height: 600px;
  overflow-y: auto;
`;

const Title = styled.h2`
  font-size: 1.8rem;
  color: var(--text-dark);
  margin-bottom: 2rem;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    bottom: -0.5rem;
    left: 0;
    width: 50px;
    height: 3px;
    background: linear-gradient(135deg, #d395ff, #ff7fd9);
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-weight: 500;
  color: var(--text-dark);
  font-size: 0.95rem;
`;

const Input = styled.input`
  padding: 0.8rem 1rem;
  border: 1px solid var(--border-light);
  border-radius: 8px;
  font-size: 1rem;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: #d395ff;
    box-shadow: 0 0 0 3px rgba(211,149,255,0.1);
  }
`;

const TextArea = styled.textarea`
  padding: 0.8rem 1rem;
  border: 1px solid var(--border-light);
  border-radius: 8px;
  font-size: 1rem;
  min-height: 100px;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: #d395ff;
    box-shadow: 0 0 0 3px rgba(211,149,255,0.1);
  }
`;

const Select = styled.select`
  padding: 0.8rem 1rem;
  border: 1px solid var(--border-light);
  border-radius: 8px;
  font-size: 1rem;
  background: white;

  &:focus {
    outline: none;
    border-color: #d395ff;
  }
`;

const Checkbox = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;

  input {
    width: 18px;
    height: 18px;
    cursor: pointer;
  }

  label {
    cursor: pointer;
  }
`;

const Button = styled(motion.button)`
  padding: 1rem;
  background: linear-gradient(135deg, #d395ff, #ff7fd9);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  margin-top: 1rem;
`;

const AdminKeyInput = styled(Input)`
  border: 2px solid #d395ff;
  background: #f8f0ff;
`;

const ProductList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const ProductItem = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: #f8f8f8;
  border-radius: 8px;
  border: 1px solid var(--border-light);

  img {
    width: 60px;
    height: 60px;
    object-fit: cover;
    border-radius: 8px;
  }
`;

const ProductInfo = styled.div`
  flex: 1;

  h4 {
    font-size: 1.1rem;
    color: var(--text-dark);
    margin-bottom: 0.3rem;
  }

  p {
    color: var(--text-gray);
    font-size: 0.9rem;
  }

  .category-badge {
    display: inline-block;
    padding: 0.2rem 0.5rem;
    background: #d395ff20;
    color: #d395ff;
    border-radius: 4px;
    font-size: 0.8rem;
    margin-top: 0.3rem;
    margin-right: 0.5rem;
  }

  .featured-badge {
    display: inline-block;
    padding: 0.2rem 0.5rem;
    background: #ff7fd920;
    color: #ff7fd9;
    border-radius: 4px;
    font-size: 0.8rem;
    margin-top: 0.3rem;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const IconButton = styled(motion.button)`
  padding: 0.5rem;
  background: white;
  border: 1px solid var(--border-light);
  border-radius: 6px;
  cursor: pointer;
  color: ${props => props.danger ? '#ff4444' : '#666'};
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: ${props => props.danger ? '#ff4444' : '#d395ff'};
    color: white;
    border-color: transparent;
  }
`;

const Message = styled(motion.div)`
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1rem;
  text-align: center;
  font-weight: 500;

  &.success {
    background: #b0ff64;
    color: #0a0a0a;
  }

  &.error {
    background: #ff4444;
    color: white;
  }
`;

const EditForm = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: white;
  padding: 2rem;
  border-radius: 16px;
  box-shadow: var(--shadow-lg);
  width: 90%;
  max-width: 500px;
  z-index: 1000;
`;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.5);
  z-index: 999;
`;

const AddProduct = () => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: '',
    is_featured: false,
    image: null
  });
  
  const [adminKey, setAdminKey] = useState('');
  const [message, setMessage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [editingProduct, setEditingProduct] = useState(null);
  const [editFormData, setEditFormData] = useState({
    name: '',
    description: '',
    category: '',
    is_featured: false
  });

  const categories = ['Fruity', 'Chocolate', 'Mint', 'Classic', 'Sour', 'Signature', 'Luxury', 'Artisan'];

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await fetch('https://kemlo.in/backend/api/products.php');
      const data = await response.json();
      setProducts(data);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : type === 'file' ? files[0] : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);
    
    const productData = new FormData();
    productData.append('name', formData.name);
    productData.append('description', formData.description);
    productData.append('category', formData.category);
    productData.append('is_featured', formData.is_featured ? '1' : '0');
    productData.append('admin_key', adminKey);
    if (formData.image) {
      productData.append('image', formData.image);
    }
    
    try {
      const response = await fetch('https://kemlo.in/backend/api/add-product.php', {
        method: 'POST',
        body: productData
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setMessage({ type: 'success', text: 'Product added successfully!' });
        setFormData({
          name: '',
          description: '',
          category: '',
          is_featured: false,
          image: null
        });
        fetchProducts();
      } else {
        setMessage({ type: 'error', text: data.error || 'Error adding product' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Error connecting to server' });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    
    try {
      const response = await fetch(`https://kemlo.in/backend/api/delete-product.php?id=${id}`, {
        method: 'DELETE',
        headers: {
          'admin_key': adminKey
        }
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setMessage({ type: 'success', text: 'Product deleted successfully!' });
        fetchProducts();
      } else {
        setMessage({ type: 'error', text: data.error || 'Error deleting product' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Error connecting to server' });
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setEditFormData({
      name: product.name,
      description: product.description,
      category: product.category,
      is_featured: product.is_featured === '1' || product.is_featured === 1 || product.is_featured === true
    });
  };

  const handleEditChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleEditSubmit = async () => {
  try {
    const response = await fetch(`https://kemlo.in/backend/api/update-product.php`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        id: editingProduct.id,
        name: editFormData.name,
        description: editFormData.description,
        category: editFormData.category,
        is_featured: editFormData.is_featured ? 1 : 0,
        admin_key: adminKey
      })
    });
      
      const data = await response.json();
      
      if (response.ok) {
        setMessage({ type: 'success', text: 'Product updated successfully!' });
        setEditingProduct(null);
        fetchProducts();
      } else {
        setMessage({ type: 'error', text: data.error || 'Error updating product' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Error connecting to server' });
    }
  };

  return (
    <Container>
      <AdminContainer>
        <FormSection
          initial={{ opacity: 0, x: -30 }}
          animate={{ opacity: 1, x: 0 }}
        >
          <Title>Add New Product</Title>
          
          {message && (
            <Message
              className={message.type}
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              {message.text}
            </Message>
          )}
          
          <Form onSubmit={handleSubmit}>
            <FormGroup>
              <Label>Admin Key</Label>
              <AdminKeyInput
                type="password"
                value={adminKey}
                onChange={(e) => setAdminKey(e.target.value)}
                required
                placeholder="Enter admin key"
              />
            </FormGroup>
            
            <FormGroup>
              <Label>Product Name</Label>
              <Input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                placeholder="e.g., Rainbow Swirl"
              />
            </FormGroup>
            
            <FormGroup>
              <Label>Description</Label>
              <TextArea
                name="description"
                value={formData.description}
                onChange={handleChange}
                required
                placeholder="Describe this delicious candy..."
              />
            </FormGroup>
            
            <FormGroup>
              <Label>Category</Label>
              <Select
                name="category"
                value={formData.category}
                onChange={handleChange}
                required
              >
                <option value="">Select a category</option>
                {categories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </Select>
            </FormGroup>
            
            <FormGroup>
              <Label>Product Image</Label>
              <Input
                type="file"
                name="image"
                onChange={handleChange}
                accept="image/*"
                required
              />
            </FormGroup>
            
            <Checkbox>
              <input
                type="checkbox"
                name="is_featured"
                id="is_featured"
                checked={formData.is_featured}
                onChange={handleChange}
              />
              <Label htmlFor="is_featured">Feature this product</Label>
            </Checkbox>
            
            <Button
              type="submit"
              disabled={loading}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {loading ? 'Adding...' : 'Add Product'}
            </Button>
          </Form>
        </FormSection>

        <ProductsSection
          initial={{ opacity: 0, x: 30 }}
          animate={{ opacity: 1, x: 0 }}
        >
          <Title>Manage Products</Title>
          
          <ProductList>
            {products.map(product => {
              // Ensure is_featured is treated as boolean
              const isFeatured = product.is_featured === '1' || product.is_featured === 1 || product.is_featured === true;
              
              return (
                <ProductItem
                  key={product.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  <img 
  src={product.image_url ? `https://kemlo.in/backend${product.image_url}` : 'https://placehold.co/60x60/d395ff/white?text=Candy'} 
  alt={product.name}
  onError={(e) => {
    e.target.src = 'https://placehold.co/60x60/d395ff/white?text=Candy';
  }}
/>
                  <ProductInfo>
                    <h4>{product.name}</h4>
                    <p>{product.description?.substring(0, 50)}...</p>
                    <span className="category-badge">{product.category}</span>
                    {isFeatured && <span className="featured-badge">Featured</span>}
                  </ProductInfo>
                  <ActionButtons>
                    <IconButton
                      onClick={() => handleEdit(product)}
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                    >
                      <FiEdit2 />
                    </IconButton>
                    <IconButton
                      danger
                      onClick={() => handleDelete(product.id)}
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                    >
                      <FiTrash2 />
                    </IconButton>
                  </ActionButtons>
                </ProductItem>
              );
            })}
          </ProductList>
        </ProductsSection>
      </AdminContainer>

      {editingProduct && (
        <>
          <Overlay onClick={() => setEditingProduct(null)} />
          <EditForm>
            <Title>Edit Product</Title>
            <FormGroup>
              <Label>Product Name</Label>
              <Input
                type="text"
                name="name"
                value={editFormData.name}
                onChange={handleEditChange}
              />
            </FormGroup>
            <FormGroup>
              <Label>Description</Label>
              <TextArea
                name="description"
                value={editFormData.description}
                onChange={handleEditChange}
              />
            </FormGroup>
            <FormGroup>
              <Label>Category</Label>
              <Select
                name="category"
                value={editFormData.category}
                onChange={handleEditChange}
              >
                {categories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </Select>
            </FormGroup>
            <Checkbox>
              <input
                type="checkbox"
                name="is_featured"
                id="edit_featured"
                checked={editFormData.is_featured}
                onChange={handleEditChange}
              />
              <Label htmlFor="edit_featured">Feature this product</Label>
            </Checkbox>
            <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
              <Button
                onClick={handleEditSubmit}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <FiSave /> Save Changes
              </Button>
              <Button
                onClick={() => setEditingProduct(null)}
                style={{ background: '#666' }}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <FiX /> Cancel
              </Button>
            </div>
          </EditForm>
        </>
      )}
    </Container>
  );
};

export default AddProduct;